import { useState, useEffect } from 'react';

export default function HomeAbout() {
  const [animatedNumbers, setAnimatedNumbers] = useState({
    years: 0,
    educators: 0,
    universities: 0,
    countries: 0
  });

  // Effect for animating numbers
  useEffect(() => {
    let observer;
    const animateNumbers = () => {
      const targets = { years: 10, educators: 150, universities: 200, countries: 25 };
      const duration = 2000;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        setAnimatedNumbers({
          years: Math.floor(targets.years * progress),
          educators: Math.floor(targets.educators * progress),
          universities: Math.floor(targets.universities * progress),
          countries: Math.floor(targets.countries * progress)
        });

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      const timer = setTimeout(animate, 500);
      return () => clearTimeout(timer);
    };
    
    const container = document.querySelector('[data-component="container"]');
    if (container) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            animateNumbers();
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(container);
    }
    
    return () => {
      if (container && observer) {
        observer.unobserve(container);
      }
    };
  }, []);

  // Effect for injecting stylesheet
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.setAttribute('data-ai-society-styles', 'true');
    // --- UPDATED STYLESHEET WITH !important RULES ---
    styleSheet.textContent = `
      /* Keyframe Animations */
      @keyframes float1 { 0%, 100% { transform: translateY(0px) translateX(0px); } 33% { transform: translateY(-15px) translateX(8px); } 66% { transform: translateY(8px) translateX(-5px); } }
      @keyframes float2 { 0%, 100% { transform: translateY(0px) translateX(0px); } 50% { transform: translateY(-12px) translateX(-6px); } }
      @keyframes float3 { 0%, 100% { transform: translateY(0px) translateX(0px); } 25% { transform: translateY(10px) translateX(4px); } 75% { transform: translateY(-8px) translateX(-8px); } }
      @keyframes pulse { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.9; } 50% { transform: translate(-50%, -50%) scale(1.05); opacity: 1; } }
      @keyframes connectionPulse { 0%, 100% { opacity: 0.7; } 50% { opacity: 1; } }
      @keyframes bookFloat { 0%, 100% { transform: translateX(-50%) translateY(0px); } 50% { transform: translateX(-50%) translateY(-8px); } }

      /* Tablet Styles (Below 1000px) */
      @media (max-width: 1050px) {
        [data-component="mainContent"] {
          grid-template-columns: 1fr !important; /* Forces single-column layout */
          gap: 40px !important;
        }
        [data-component="rightColumn"] {
          order: 1; /* Animation moves to the top */
        }
        [data-component="leftColumn"] {
          order: 2; /* Text content moves below */
          padding-right: 0 !important; /* Removes desktop padding */
        }
        [data-component="heroImageContainer"] {
          height: 400px;
          max-width: 500px;
          margin: 0 auto;
        }
        [data-component="statsContainer"] {
          display: none !important;
        }
        [data-component="featuresGrid"] {
          grid-template-columns: repeat(2, 1fr) !important;
        }
        [data-component="mainHeading"] {
          font-size: 3rem !important;
        }
      }

      /* Mobile Styles (Below 768px) */
      @media (max-width: 768px) {
        [data-component="content"] {
          padding: 40px 20px !important;
        }
        [data-component="mainContent"] {
          gap: 30px !important;
          margin-bottom: 40px !important;
        }
        [data-component="mainHeading"] {
          font-size: 2.5rem !important;
        }
        [data-component="featuresGrid"] {
          grid-template-columns: 1fr !important;
        }
        [data-component="heroImageContainer"] {
          height: 350px !important;
        }
      }

      /* Small Mobile Styles (Below 480px) */
      @media (max-width: 480px) {
        [data-component="content"] {
          padding: 30px 16px !important;
        }
        [data-component="mainHeading"] {
          font-size: 2.2rem !important;
        }
        [data-component="heroImageContainer"] {
          height: 300px !important;
        }
      }
    `;
    if (!document.head.querySelector('[data-ai-society-styles]')) {
      document.head.appendChild(styleSheet);
    }
    return () => {
      const styleTag = document.head.querySelector('[data-ai-society-styles]');
      if (styleTag) {
        document.head.removeChild(styleTag);
      }
    };
  }, []);

  return (
    <div style={styles.container} data-component="container">
      <div style={styles.content} data-component="content">
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.aboutBadge} data-component="aboutBadge">
            <span style={styles.aboutIcon}>ℹ️</span>
            About Global AI Society
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.mainContent} data-component="mainContent">
          <div style={styles.leftColumn} data-component="leftColumn">
            <h1 style={styles.mainHeading} data-component="mainHeading">
              Leading the way in <span style={styles.highlightText}>ethical AI education</span>
            </h1>
            
            <p style={styles.description} data-component="description">
              Global AI Society is a pioneering educational foundation dedicated to 
              democratizing artificial intelligence knowledge. We believe that AI 
              working for the benefit of humanity must be designed with inclusivity at its core.
            </p>

            <p style={styles.description} data-component="description">
              Our vision is to create a world where AI education is accessible to 
              everyone, fostering a generation of ethical AI practitioners who will 
              shape the future of technology with wisdom and responsibility.
            </p>

            <div style={styles.featuresGrid} data-component="featuresGrid">
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>💙</div>
                <div>
                  <h3 style={styles.featureTitle}>Ethical AI</h3>
                  <p style={styles.featureDescription}>Promoting responsible AI development with human values at the center</p>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>🌍</div>
                <div>
                  <h3 style={styles.featureTitle}>Global Reach</h3>
                  <p style={styles.featureDescription}>Connecting learners and researchers from every corner of the world</p>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>💡</div>
                <div>
                  <h3 style={styles.featureTitle}>Innovation</h3>
                  <p style={styles.featureDescription}>Fostering breakthrough research and cutting-edge educational methods</p>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>👥</div>
                <div>
                  <h3 style={styles.featureTitle}>Community</h3>
                  <p style={styles.featureDescription}>Building inclusive spaces for collaboration and knowledge sharing</p>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.rightColumn} data-component="rightColumn">
            <div style={styles.heroImageContainer} data-component="heroImageContainer">
              <div style={styles.heroImage}>
                <div style={styles.particle1}></div>
                <div style={styles.particle2}></div>
                <div style={styles.particle3}></div>
                <div style={styles.particle4}></div>
                <div style={styles.particle5}></div>
                <div style={styles.particle6}></div>
                <div style={styles.particle7}></div>
                <div style={styles.particle8}></div>
                <div style={styles.particle9}></div>
                <div style={styles.particle10}></div>
                <div style={styles.particle11}></div>
                <div style={styles.particle12}></div>
                <div style={styles.brainContainer}>
                  <div style={styles.brainCore}></div>
                  <div style={styles.brainConnection1}></div>
                  <div style={styles.brainConnection2}></div>
                  <div style={styles.brainConnection3}></div>
                </div>
                <div style={styles.bookContainer}>
                  <div style={styles.bookLeft}></div>
                  <div style={styles.bookRight}></div>
                  <div style={styles.bookSpine}></div>
                  <div style={styles.bookPage1}></div>
                  <div style={styles.bookPage2}></div>
                  <div style={styles.bookPage3}></div>
                </div>
              </div>
              <div style={styles.statsContainer} data-component="statsContainer">
                <div style={styles.statItem}>
                  <div style={styles.statNumber}>{animatedNumbers.years}+</div>
                  <div style={styles.statLabel}>Years of Excellence</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statNumber}>{animatedNumbers.educators}+</div>
                  <div style={styles.statLabel}>Expert Educators</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statNumber}>{animatedNumbers.universities}+</div>
                  <div style={styles.statLabel}>Partner Universities</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statNumber}>{animatedNumbers.countries}+</div>
                  <div style={styles.statLabel}>Countries Reached</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div style={styles.missionSection}>
          <div style={styles.missionBadge} data-component="missionBadge">
            <span style={styles.missionIcon}>🎯</span>
            Our Mission
          </div>
          <blockquote style={styles.missionQuote} data-component="missionQuote">
            "To democratize AI education globally, ensuring that the transformative power of 
            artificial intelligence is harnessed for the betterment of humanity through ethical, 
            inclusive, and innovative learning experiences."
          </blockquote>
        </div>
      </div>
    </div>
  );
}

// Main styles object (remains unchanged)
const styles = {
  container: {
    minHeight: '100vh',
    background: '#ffffff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#333',
  },
  content: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '60px 40px',
  },
  header: {
    marginBottom: '40px'
  },
  aboutBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '20px',
    padding: '8px 16px',
    fontSize: '14px',
    color: '#8B5A2B',
    fontWeight: '500',
  },
  aboutIcon: {
    fontSize: '16px',
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    marginBottom: '100px',
  },
  leftColumn: {
    paddingRight: '20px',
  },
  rightColumn: {
    position: 'relative',
  },
  mainHeading: {
    fontSize: '3.5rem',
    fontWeight: '800',
    lineHeight: '1.1',
    marginBottom: '32px',
    color: '#2d3748',
  },
  highlightText: {
    color: '#6366f1'
  },
  description: {
    fontSize: '1.1rem',
    lineHeight: '1.8',
    marginBottom: '24px',
    color: '#64748b',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginTop: '48px',
  },
  featureItem: {
    display: 'flex',
    gap: '16px',
  },
  featureIcon: {
    fontSize: '24px',
    flexShrink: 0,
    marginTop: '4px'
  },
  featureTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#1a202c'
  },
  featureDescription: {
    fontSize: '0.95rem',
    color: '#64748b',
    lineHeight: '1.6',
    margin: 0
  },
  heroImageContainer: {
    position: 'relative',
    width: '100%',
    height: '500px',
  },
  heroImage: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
    borderRadius: '24px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  particle1: { position: 'absolute', width: '12px', height: '12px', background: '#ff6b6b', borderRadius: '50%', top: '15%', left: '20%', animation: 'float1 6s ease-in-out infinite', boxShadow: '0 0 15px rgba(255,107,107,0.6)' },
  particle2: { position: 'absolute', width: '8px', height: '8px', background: '#4ecdc4', borderRadius: '50%', top: '25%', right: '15%', animation: 'float2 4s ease-in-out infinite', boxShadow: '0 0 12px rgba(78,205,196,0.6)' },
  particle3: { position: 'absolute', width: '10px', height: '10px', background: '#45b7d1', borderRadius: '50%', top: '35%', left: '15%', animation: 'float3 5s ease-in-out infinite', boxShadow: '0 0 14px rgba(69,183,209,0.6)' },
  particle4: { position: 'absolute', width: '6px', height: '6px', background: '#f9ca24', borderRadius: '50%', top: '45%', right: '25%', animation: 'float1 7s ease-in-out infinite', boxShadow: '0 0 10px rgba(249,202,36,0.6)' },
  particle5: { position: 'absolute', width: '14px', height: '14px', background: '#a55eea', borderRadius: '50%', top: '20%', right: '35%', animation: 'float2 5.5s ease-in-out infinite', boxShadow: '0 0 18px rgba(165,94,234,0.6)' },
  particle6: { position: 'absolute', width: '9px', height: '9px', background: '#26de81', borderRadius: '50%', top: '55%', left: '25%', animation: 'float3 4.5s ease-in-out infinite', boxShadow: '0 0 13px rgba(38,222,129,0.6)' },
  particle7: { position: 'absolute', width: '7px', height: '7px', background: '#fd79a8', borderRadius: '50%', bottom: '35%', right: '20%', animation: 'float1 6.5s ease-in-out infinite', boxShadow: '0 0 11px rgba(253,121,168,0.6)' },
  particle8: { position: 'absolute', width: '11px', height: '11px', background: '#fdcb6e', borderRadius: '50%', bottom: '25%', left: '30%', animation: 'float2 5s ease-in-out infinite', boxShadow: '0 0 15px rgba(253,203,110,0.6)' },
  particle9: { position: 'absolute', width: '8px', height: '8px', background: '#6c5ce7', borderRadius: '50%', bottom: '40%', right: '35%', animation: 'float3 4.8s ease-in-out infinite', boxShadow: '0 0 12px rgba(108,92,231,0.6)' },
  particle10: { position: 'absolute', width: '13px', height: '13px', background: '#00b894', borderRadius: '50%', top: '60%', left: '45%', animation: 'float1 5.3s ease-in-out infinite', boxShadow: '0 0 16px rgba(0,184,148,0.6)' },
  particle11: { position: 'absolute', width: '9px', height: '9px', background: '#e17055', borderRadius: '50%', top: '70%', right: '40%', animation: 'float2 6.2s ease-in-out infinite', boxShadow: '0 0 13px rgba(225,112,85,0.6)' },
  particle12: { position: 'absolute', width: '10px', height: '10px', background: '#00cec9', borderRadius: '50%', bottom: '60%', left: '40%', animation: 'float3 4.3s ease-in-out infinite', boxShadow: '0 0 14px rgba(0,206,201,0.6)' },
  brainContainer: { position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '100px', height: '100px' },
  brainCore: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '60px', height: '60px', background: 'rgba(255,255,255,0.9)', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.7)', animation: 'pulse 3s ease-in-out infinite', boxShadow: '0 0 20px rgba(255,255,255,0.8)' },
  brainConnection1: { position: 'absolute', top: '20%', left: '50%', width: '2px', height: '25px', background: 'rgba(255,255,255,0.7)', transform: 'translateX(-50%) rotate(30deg)', animation: 'connectionPulse 2s ease-in-out infinite' },
  brainConnection2: { position: 'absolute', bottom: '20%', right: '25%', width: '2px', height: '20px', background: 'rgba(255,255,255,0.7)', transform: 'rotate(-45deg)', animation: 'connectionPulse 2.5s ease-in-out infinite' },
  brainConnection3: { position: 'absolute', bottom: '25%', left: '20%', width: '2px', height: '18px', background: 'rgba(255,255,255,0.7)', transform: 'rotate(60deg)', animation: 'connectionPulse 3s ease-in-out infinite' },
  bookContainer: { position: 'absolute', bottom: '20%', left: '50%', transform: 'translateX(-50%)', width: '80px', height: '50px' },
  bookLeft: { position: 'absolute', left: '0', top: '0', width: '38px', height: '50px', background: 'linear-gradient(45deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))', borderRadius: '4px 0 0 4px', border: '1px solid rgba(255,255,255,0.8)', transform: 'perspective(100px) rotateY(-15deg)', animation: 'bookFloat 4s ease-in-out infinite' },
  bookRight: { position: 'absolute', right: '0', top: '0', width: '38px', height: '50px', background: 'linear-gradient(-45deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))', borderRadius: '0 4px 4px 0', border: '1px solid rgba(255,255,255,0.8)', transform: 'perspective(100px) rotateY(15deg)', animation: 'bookFloat 4s ease-in-out infinite' },
  bookSpine: { position: 'absolute', left: '50%', top: '0', width: '4px', height: '50px', background: 'rgba(255,255,255,0.9)', transform: 'translateX(-50%)', animation: 'bookFloat 4s ease-in-out infinite' },
  bookPage1: { position: 'absolute', left: '8px', top: '10px', width: '20px', height: '2px', background: 'rgba(255,255,255,0.6)', borderRadius: '1px' },
  bookPage2: { position: 'absolute', left: '8px', top: '18px', width: '15px', height: '2px', background: 'rgba(255,255,255,0.6)', borderRadius: '1px' },
  bookPage3: { position: 'absolute', right: '8px', top: '10px', width: '20px', height: '2px', background: 'rgba(255,255,255,0.6)', borderRadius: '1px' },
  statsContainer: {
    position: 'absolute',
    bottom: '-60px',
    left: '0',
    right: '0',
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
  },
  statItem: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '20px 16px',
    textAlign: 'center',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#6366f1',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontWeight: '500',
  },
  missionSection: {
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    marginTop: '60px',
  },
  missionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '20px',
    padding: '8px 16px',
    fontSize: '14px',
    color: '#8B5A2B',
    fontWeight: '500',
    marginBottom: '32px',
  },
  missionIcon: {
    fontSize: '16px'
  },
  missionQuote: {
    fontSize: '1.25rem',
    fontStyle: 'italic',
    lineHeight: '1.7',
    color: '#374151',
    margin: 0,
    fontWeight: '400',
  }
};